import { FC, InputHTMLAttributes, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { COLORS, TYPOGRAPHY } from '@app/constants';
import { sortingOptions } from '../../constants';
import { BreakpointsEnum } from '@app/types';
import Radio from '@app/ui/components/Radio';
import Button from '@app/ui/components/Button';

interface FormValues {
  sort: string;
}

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  className?: string;
  onSorting?: (data: FormValues) => void;
  defaultValues?: FormValues;
  applyAndSaveAllSelectedFilters?: () => void;
}

const Sorting: FC<Props> = ({
  className,
  onSorting,
  defaultValues,
  applyAndSaveAllSelectedFilters,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
  } = useForm<FormValues>({
    defaultValues,
  });

  const onSubmit = (data: FormValues) => {
    // reset();
    applyAndSaveAllSelectedFilters && applyAndSaveAllSelectedFilters();
  };

  const watchSort = watch('sort');

  useEffect(() => {
    if (onSorting) onSorting({ sort: watchSort });
  }, [watchSort]);

  return (
    <Root className={className} onSubmit={handleSubmit(onSubmit)}>
      <Heading>Сортировать</Heading>
      <RadioButtons>
        {sortingOptions.map(({ text, value }) => (
          <StyledRadio
            key={text}
            text={text}
            value={value}
            {...register('sort')}
          />
        ))}
      </RadioButtons>
      <StyledButton styleType="primary" isDisabled={!isDirty}>
        Применить
      </StyledButton>
    </Root>
  );
};

Sorting.displayName = 'Sorting';

const Root = styled.form`
  width: 270px;
  padding: 24px 20px;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.elementsStatesFocusedBorder};
  border-radius: 12px;
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 2;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    position: relative;
    width: 100%;
    padding: 26px 0 0 0;
    top: 0;
    border: none;
  }
`;

const Heading = styled.h3`
  margin-bottom: 24px;
  ${TYPOGRAPHY.headline1Semibold18};
  color: ${COLORS.black};

  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: none;
  }
`;

const RadioButtons = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  padding: 0 4px;
`;

const StyledRadio = styled(Radio)`
  margin-bottom: 12px;
  align-items: flex-start;
  div {
    min-width: 16px;
    min-height: 16px;
    margin-top: 2px;
  }
  span {
    margin-left: 10px;
    ${TYPOGRAPHY.footnoteRegular13};
    color: ${COLORS.grayscale600};
  }
  &:last-of-type {
    margin-bottom: 0;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  padding: 12px;
  ${TYPOGRAPHY.body1Medium14};
`;

export default Sorting;
